import { Tabs } from 'expo-router';
import { Platform, View, Text, StyleSheet } from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { useNotificationsStore } from '@/store/notifications';

type IconName = keyof typeof Ionicons.glyphMap;

function TabIcon({
  icon,
  custom,
  label,
  focused,
  color,
  badge,
}: {
  icon?: IconName;
  custom?: 'basketball' | 'football';
  label: string;
  focused: boolean;
  color: string;
  badge?: number;
}) {
  const iconColor = focused ? color : Colors.textMuted;

  return (
    <View style={styles.tabItem}>
      <View style={[styles.iconShell, focused && { backgroundColor: 'rgba(82,255,39,0.1)' }]}>
        {custom ? (
          <MaterialCommunityIcons name={custom} size={22} color={iconColor} />
        ) : (
          <Ionicons name={icon ?? 'ellipse'} size={22} color={iconColor} />
        )}
        {badge !== undefined && badge > 0 && (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{badge > 9 ? '9+' : badge}</Text>
          </View>
        )}
      </View>
      <Text style={[styles.label, { color: focused ? color : Colors.textMuted }]} numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

function AlertasIcon({ focused }: { focused: boolean }) {
  const unread = useNotificationsStore((s) => s.unreadCount);
  return <TabIcon icon="notifications-outline" label="Alertas" focused={focused} color="#ff4d6d" badge={unread} />;
}

export default function TabsLayout() {
  return (
    <Tabs
      screenOptions={{
        headerShown: false,
        tabBarStyle: {
          position: 'absolute',
          left: 10,
          right: 10,
          bottom: Platform.OS === 'ios' ? 14 : 8,
          height: Platform.OS === 'ios' ? 76 : 64,
          paddingTop: 7,
          paddingBottom: Platform.OS === 'ios' ? 16 : 7,
          backgroundColor: Colors.tabBar,
          borderTopWidth: 1,
          borderTopColor: Colors.tabBarBorder,
          borderRadius: 18,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 10 },
          shadowOpacity: 0.35,
          shadowRadius: 18,
          elevation: 14,
        },
        tabBarActiveTintColor: Colors.futebol,
        tabBarShowLabel: false,
      }}
    >
      <Tabs.Screen
        name="futebol"
        options={{
          title: 'Inicio',
          tabBarIcon: ({ focused }: { focused: boolean }) => (
            <TabIcon icon="home" label="Inicio" focused={focused} color={Colors.futebol} />
          ),
        }}
      />
      <Tabs.Screen
        name="copa"
        options={{
          title: 'Jogos',
          tabBarIcon: ({ focused }: { focused: boolean }) => (
            <TabIcon icon="calendar-outline" label="Jogos" focused={focused} color={Colors.futebol} />
          ),
        }}
      />
      <Tabs.Screen
        name="nba"
        options={{
          title: 'NBA',
          tabBarIcon: ({ focused }: { focused: boolean }) => (
            <TabIcon custom="basketball" label="NBA" focused={focused} color={Colors.nba} />
          ),
        }}
      />
      <Tabs.Screen
        name="nfl"
        options={{
          title: 'NFL',
          tabBarIcon: ({ focused }: { focused: boolean }) => (
            <TabIcon custom="football" label="NFL" focused={focused} color={Colors.nfl} />
          ),
        }}
      />
      <Tabs.Screen
        name="favoritos"
        options={{
          title: 'Favoritos',
          tabBarIcon: ({ focused }: { focused: boolean }) => (
            <TabIcon icon="star-outline" label="Favoritos" focused={focused} color="#facc15" />
          ),
        }}
      />
      <Tabs.Screen
        name="alertas"
        options={{
          title: 'Alertas',
          tabBarIcon: ({ focused }: { focused: boolean }) => <AlertasIcon focused={focused} />,
        }}
      />
    </Tabs>
  );
}

const styles = StyleSheet.create({
  tabItem: {
    width: 58,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  iconShell: {
    width: 34,
    height: 30,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    width: 58,
    textAlign: 'center',
    fontSize: 9,
    fontWeight: '800',
  },
  badge: {
    position: 'absolute',
    top: -3,
    right: -5,
    minWidth: 15,
    height: 15,
    borderRadius: 8,
    backgroundColor: '#ff4d6d',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
    borderWidth: 1,
    borderColor: Colors.tabBar,
  },
  badgeText: { color: Colors.white, fontSize: 8, fontWeight: '900' },
});
